import {computed, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {patchState, signalStore, withComputed, withHooks, withMethods, withState} from '@ngrx/signals';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {distinctUntilChanged, EMPTY, fromEvent, map, merge, pipe, startWith, switchMap, tap, throttleTime, timer} from 'rxjs';
import {withTenantScope} from 'src/app/services/shared/with-tenant-scope';
import {CurrentTenantStore} from 'src/app/services/tenant/current-tenant.store';
import {TenantAccountService} from 'src/app/services/accounts/tenant-account.service';
import {Permission} from 'src/app/services/auth/permissions.service';
import {Account, TenantAccountActivity} from 'src/app/services/accounts/account.model';

/** KAB-2397 — a member is "active" when seen within the last 5 minutes. */
const ACTIVE_MINUTES = 5;
/** Statuses refresh every minute: someone who leaves stops showing "active" within about 6 minutes. */
const REFRESH_MS = 60_000;
/** Under an hour, the time since the last activity is rounded down to one of these steps — the last one closes the
 * active window, so every time past it finds its step. */
const MINUTE_STEPS = [30, 15, 10, ACTIVE_MINUTES];

export type ActivityStatusKey = 'active' | 'minutes' | 'hour' | 'hours' | 'day' | 'days';

export interface MemberActivity {
	account: Account;
	name: string;
	isSelf: boolean;
	active: boolean;
	/** "Actif" / "Vu il y a {{value}} …" */
	statusKey: `menu.activity.${ActivityStatusKey}`;
	statusValue: number;
}

/** "Active" within the last 5 minutes, otherwise an approximate time since: 5, 10, 15 or 30 minutes, then hours, then days. */
export function activityStatus(lastActivityAt: number, now: number): {key: ActivityStatusKey; value: number} {
	const minutes = Math.floor((now - lastActivityAt) / 60_000);
	if (minutes < ACTIVE_MINUTES) {
		return {key: 'active', value: 0};
	} else if (minutes < 60) {
		return {key: 'minutes', value: MINUTE_STEPS.find(step => minutes >= step)!};
	}
	const hours = Math.floor(minutes / 60);
	if (hours < 24) {
		return {key: hours === 1 ? 'hour' : 'hours', value: hours};
	}
	const days = Math.floor(hours / 24);
	return {key: days === 1 ? 'day' : 'days', value: days};
}

function memberActivity(account: Account, isSelf: boolean, lastActivityAt: number, now: number): MemberActivity {
	const {key, value} = activityStatus(lastActivityAt, now);
	// Invited members who never filled their name carry their email instead (as in app-display-user).
	const name = account.firstName === account.email || account.lastName === account.email
		? account.email
		: `${account.firstName} ${account.lastName}`;
	return {account, name, isSelf, active: key === 'active', statusKey: `menu.activity.${key}`, statusValue: value};
}

/**
 * Members of the active tenant seen in the audit log over the last 30 days — the header avatars and the menu's
 * profile dot. The signed-in user comes first.
 *
 * The user's own activity also counts what the browser sees (opening the app, clicks, keys, scrolls): the audit log
 * only records part of the calls, a few seconds late through PubSub, so on its own it would show the user
 * "seen 2 days ago" at the start of every session.
 */
export const TenantAccountActivityStore = signalStore(
	{providedIn: 'root'},
	withTenantScope<TenantAccountActivity, TenantAccountService>({
		service: TenantAccountService,
		selectId: activity => activity.account.accountId,
		fetch: (service, tenantId) => service.getTenantAccountsActivity(tenantId),
	}),
	withState({selfInteractionAt: Date.now()}),
	withComputed((store, tenant = inject(CurrentTenantStore)) => {
		const members = computed(() => {
			const self = tenant.currentAccount()?.account;
			// ponytail: statuses are recomputed on each refresh (and interaction), not on a ticking clock.
			const now = Date.now();
			const others = store.entities()
				.filter(activity => activity.account.accountId !== self?.accountId)
				.map(activity => memberActivity(activity.account, false, Date.parse(activity.lastActivityAt), now));
			if (!self) {
				return others;
			} else {
				const selfAuditAt = store.entityMap()[self.accountId]?.lastActivityAt;
				const selfLastAt = Math.max(store.selfInteractionAt(), selfAuditAt ? Date.parse(selfAuditAt) : 0);
				return [memberActivity(self, true, selfLastAt, now), ...others];
			}
		});
		return {
			/** Members' presence is member data: shown to those who can read the members, never while suspended. */
			canRead: computed(() => tenant.hasPermission(Permission.MEMBERS_READ) && !tenant.isSuspended()),
			members,
			/** Until the account is known the user has just opened the app: active. */
			selfActive: computed(() => members().find(member => member.isSelf)?.active ?? true),
		};
	}),
	withMethods((store, tenant = inject(CurrentTenantStore)) => {
		// Paused while the tab is hidden (a background tab would poll for nobody); coming back refreshes at once.
		const tabVisible$ = fromEvent(document, 'visibilitychange').pipe(
			startWith(null),
			map(() => document.visibilityState === 'visible'),
			distinctUntilChanged(),
		);
		const pollTenant = rxMethod<string | undefined>(pipe(
			switchMap(tenantId => tenantId ? tabVisible$ : EMPTY),
			switchMap(visible => visible ? timer(0, REFRESH_MS) : EMPTY),
			tap(() => store.refresh()),
		));
		return {
			/** Refreshes the statuses every minute for as long as the calling view lives (call it from a constructor). */
			poll(): void {
				pollTenant(computed(() => store.canRead() ? tenant.currentTenantId() : undefined));
			},
		};
	}),
	withHooks({
		onInit(store) {
			merge(fromEvent(document, 'pointerdown'), fromEvent(document, 'keydown'), fromEvent(document, 'wheel'))
				.pipe(throttleTime(30_000, undefined, {leading: true, trailing: true}), takeUntilDestroyed())
				.subscribe(() => patchState(store, {selfInteractionAt: Date.now()}));
		},
	}),
);
